import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, ChevronRight, FileSearch, ShieldCheck, Truck, ClipboardList, Search, Package, FileText, Scale } from "lucide-react";
import { Seo, serviceSchema, breadcrumbSchema } from "../components/site/Seo";
import SEO_ROUTES from "../seo-routes.json";
import { PageHero, BulletGrid, CTABand, SectionHead, Prose } from "./InfoPages";
import { CapabilityDownload } from "./CapabilityStatement";
import { track } from "../lib/track";

const crumbs = (name, path) => breadcrumbSchema([{ name: "Home", path: "/" }, { name, path }]);
export const FEDERAL_RFQ_LINK = "/request-a-quote?type=federal";

export const FEDERAL_STEPS = [
  ["Submit the Requirement", "Customer submits a solicitation, RFQ, NSN, part number, BOM, specification or purchase requirement."],
  ["Requirement Review", "PGPS reviews the requirement, quantity, delivery requirements, specifications and applicable documentation requirements."],
  ["Supplier Market Research", "PGPS identifies manufacturers, distributors and other potential commercial sources."],
  ["Source & Availability Verification", "PGPS verifies available information concerning manufacturer, part number, CAGE information when applicable, availability and lead time."],
  ["Quote Collection", "PGPS obtains supplier pricing, availability, lead time and applicable commercial information."],
  ["Compliance Review", "PGPS compares supplier responses against the stated requirement and applicable solicitation requirements."],
  ["Procurement Response", "PGPS prepares the applicable quote/procurement information."],
  ["Fulfillment & Delivery", "If awarded, PGPS coordinates supplier purchasing, logistics, tracking and delivery."],
];

export const LIFECYCLE = ["Government / Prime Contractor", "Federal / DLA or Prime RFQ", "Submit Requirement", "Requirement Review", "NSN / Part Identification",
  "Supplier Market Research", "Availability + Lead Time", "Supplier Quote Collection", "Price / Source / Document Review", "Procurement Response", "Award",
  "Supplier Purchase Order", "Inspection / Documentation", "Shipping / Logistics", "Delivery", "Tracking / Documentation", "Customer Complete"];

export function LifecycleFlow({ testid = "federal-lifecycle" }) {
  return (
    <ol className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2" data-testid={testid}>
      {LIFECYCLE.map((s, i) => (
        <li key={s} data-testid={`${testid}-step-${i}`} className={`flex items-center gap-3 rounded-xl border px-4 py-3 ${i < 2 ? "bg-[var(--pg-navy)] border-[var(--pg-navy)] text-white" : "bg-white border-slate-200 text-[var(--pg-navy)]"}`}>
          <span className={`w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold flex-shrink-0 ${i < 2 ? "bg-white/15" : "bg-[var(--pg-accent)] text-white"}`}>{i < 2 ? "•" : i - 1}</span>
          <span className="font-[Poppins] font-semibold text-[13.5px]">{s}</span>
          {i < LIFECYCLE.length - 1 && <ChevronRight size={14} className="ml-auto opacity-50 flex-shrink-0" />}
        </li>
      ))}
    </ol>
  );
}

export function FlowChain({ items, testid }) {
  return (
    <ol className="flex flex-wrap items-center gap-2" data-testid={testid}>
      {items.map((s, i) => (
        <li key={s} className="flex items-center gap-2">
          <span className="bg-white border border-slate-200 rounded-lg px-3 py-2 font-[Poppins] font-semibold text-[12.5px] text-[var(--pg-navy)]">{s}</span>
          {i < items.length - 1 && <ChevronRight size={15} className="text-[var(--pg-accent)] flex-shrink-0" />}
        </li>
      ))}
    </ol>
  );
}

export function FederalProcess({ compact = false }) {
  return (
    <section className="py-16 md:py-24 bg-white" data-testid="federal-process-section">
      <div className="pg-container">
        <SectionHead eyebrow="Federal Procurement Process" title="Eight steps from requirement to delivery"
          sub="Statuses in the Client Portal reflect the actions our team has actually completed — nothing is marked submitted or awarded automatically." />
        <div className={`mt-10 grid sm:grid-cols-2 ${compact ? "lg:grid-cols-4" : "lg:grid-cols-4"} gap-4`}>
          {FEDERAL_STEPS.map(([h, b], i) => (
            <div key={h} data-testid={`federal-step-${i}`} className="bg-[var(--pg-bg)] border border-slate-200 rounded-2xl p-6">
              <span className="font-[Poppins] font-extrabold text-[26px] text-slate-300">0{i + 1}</span>
              <h3 className="mt-2 font-[Poppins] font-semibold text-[16px] text-[var(--pg-navy)] leading-snug">{h}</h3>
              <p className="mt-2 text-[13.5px] text-[var(--pg-ink-soft)] leading-relaxed">{b}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function FederalCTA({ label = "Submit a Federal Requirement", source }) {
  return (
    <Link to={FEDERAL_RFQ_LINK} data-testid={`${source}-federal-cta`} className="btn-primary" onClick={() => track("submit_rfq_clicked", { source })}>
      {label} <ArrowRight size={18} />
    </Link>
  );
}

/* Homepage section */
const HOME_CARDS = [
  [Search, "NSN & Part Number Sourcing", "Source products using NSNs, NIINs, manufacturer part numbers and specifications."],
  [ShieldCheck, "Supplier Verification", "Research manufacturers, distributors, availability, lead times and applicable supplier documentation."],
  [Truck, "Procurement & Delivery", "Coordinate competitive sourcing, purchasing, logistics and delivery."],
];
export function FederalHighlight() {
  return (
    <section className="py-16 md:py-20 bg-[var(--pg-navy)] text-white" data-testid="home-federal-section">
      <div className="pg-container">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div className="max-w-2xl">
            <span className="eyebrow" style={{ color: "#7EC4FF" }}>Federal &amp; Defense</span>
            <h2 className="mt-4 text-[30px] md:text-[38px] font-extrabold leading-[1.1]">Federal &amp; Defense Supply Solutions</h2>
            <p className="mt-4 text-[16px] text-white/80 leading-relaxed">
              PGPS sources commercial and industrial products, components, MRO supplies, equipment and hard-to-source parts for federal agencies, government contractors and prime contractors.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <FederalCTA source="home_federal" />
            <Link to="/federal-dla" data-testid="home-federal-learn-more" className="inline-flex items-center gap-2 font-[Poppins] font-semibold px-5 py-3.5 rounded-full border border-white/35 text-white hover:bg-white hover:text-[var(--pg-navy)] transition-colors">Federal &amp; DLA page <ArrowRight size={16} /></Link>
          </div>
        </div>
        <div className="mt-10 grid md:grid-cols-3 gap-4">
          {HOME_CARDS.map(([Icon, h, b], i) => (
            <div key={h} data-testid={`home-federal-card-${i}`} className="bg-white/[0.06] border border-white/10 rounded-2xl p-6 backdrop-blur-sm">
              <div className="w-11 h-11 rounded-lg bg-[var(--pg-accent)] text-white flex items-center justify-center"><Icon size={20} /></div>
              <h3 className="mt-4 font-[Poppins] font-semibold text-[17px]">{h}</h3>
              <p className="mt-2 text-[14px] text-white/75 leading-relaxed">{b}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* Government page add-on */
export function GovFederalSection() {
  return (
    <section className="py-16 md:py-24 bg-[var(--pg-bg)]" data-testid="gov-federal-section">
      <div className="pg-container">
        <SectionHead eyebrow="Federal Supply & DLA Opportunity Support" title="Federal Supply &amp; DLA Opportunity Support"
          sub="Sourcing support for federal solicitations and DLA-related supply opportunities identified by NSN, part number or specification. Certifications, contract vehicles and past performance are listed above once documented." />
        <div className="mt-8">
          <BulletGrid testidPrefix="gov-federal-item" items={["NSN sourcing", "Part-number sourcing", "Manufacturer research", "CAGE research", "Supplier identification",
            "Availability verification", "Lead-time verification", "Quote comparison", "Documentation coordination", "Logistics coordination"]} />
        </div>
        <div className="mt-8"><FlowChain testid="gov-federal-flow" items={LIFECYCLE} /></div>
        <div className="mt-8 flex flex-wrap gap-3">
          <FederalCTA source="gov_federal" />
          <Link to="/federal-dla" data-testid="gov-federal-page-link" className="inline-flex items-center gap-2 font-[Poppins] font-semibold px-5 py-3.5 rounded-full border border-slate-300 text-[var(--pg-navy)] hover:border-[var(--pg-accent)] hover:text-[var(--pg-accent)] transition-colors">Federal &amp; DLA Supply Solutions <ArrowRight size={16} /></Link>
        </div>
      </div>
    </section>
  );
}

/* Small add-on used on Procurement / MRO / Logistics pages */
export function FederalNote({ title, text, tags, items, testid }) {
  return (
    <section className="py-14 bg-[var(--pg-bg)]" data-testid={testid}>
      <div className="pg-container">
        <div className="bg-white border border-slate-200 rounded-2xl p-7 md:p-9 grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <span className="eyebrow">Federal &amp; Defense</span>
            <h2 className="mt-3 font-[Poppins] font-bold text-[22px] md:text-[26px] text-[var(--pg-navy)] leading-tight">{title}</h2>
            <p className="mt-3 text-[15.5px] text-[var(--pg-ink-soft)] leading-relaxed">{text}</p>
            {tags && <div className="mt-4 flex flex-wrap gap-2">{tags.map((t) => <span key={t} className="font-[Poppins] font-semibold text-[12px] bg-[var(--pg-bg)] border border-slate-200 text-[var(--pg-navy)] px-3 py-1.5 rounded-full">{t}</span>)}</div>}
            {items && <ul className="mt-4 grid sm:grid-cols-2 gap-2">{items.map((it) => <li key={it} className="flex items-start gap-2 text-[14px] text-[var(--pg-navy)]"><CheckCircle2 size={16} className="text-[var(--pg-accent)] mt-0.5 flex-shrink-0" />{it}</li>)}</ul>}
          </div>
          <div className="flex flex-col justify-center gap-3">
            <FederalCTA source={testid} />
            <Link to="/federal-dla" className="text-[13.5px] font-[Poppins] font-semibold text-[var(--pg-navy)] hover:text-[var(--pg-accent)] inline-flex items-center gap-1.5 px-2">Learn about federal supply support <ArrowRight size={14} /></Link>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------------- /federal-dla ---------------- */
const FED_CAPS = ["NSN / NIIN sourcing", "Part-number sourcing", "Manufacturer identification", "CAGE-code research", "Supplier identification", "Supplier verification",
  "Availability verification", "Lead-time verification", "Competitive supplier quotes", "Quote comparison", "Industrial & MRO supplies", "Electrical and electronic components",
  "Equipment and replacement parts", "Safety and facility supplies", "IT equipment", "Logistics and delivery coordination"];

export function FederalDLAPage() {
  const desc = SEO_ROUTES["/federal-dla"].description;
  return (
    <div data-testid="federal-dla-page">
      <Seo route="/federal-dla" jsonLd={[serviceSchema({ name: "Federal & DLA Supply Solutions", description: desc, path: "/federal-dla" }), crumbs("Federal & DLA", "/federal-dla")]} />
      <section data-testid="federal-hero" className="pt-[130px] pb-16 hero-bg">
        <div className="pg-container max-w-4xl">
          <span className="eyebrow" style={{ color: "#7EC4FF" }}>Federal &amp; DLA Supply Solutions</span>
          <h1 className="mt-5 text-[36px] md:text-[50px] font-extrabold leading-[1.06] text-white">Federal &amp; Defense Supply Solutions</h1>
          <p className="mt-4 text-[19px] md:text-[22px] font-[Poppins] font-semibold text-[#7EC4FF] leading-snug">NSN, Part Number, MRO, Equipment and Component Sourcing for Federal Agencies and Prime Contractors</p>
          <p className="mt-5 text-[17px] text-white/80 max-w-3xl leading-relaxed">
            Patrick Global Procurement Services supports federal agencies, government contractors and prime contractors with sourcing, supplier identification, quote comparison, availability verification, procurement coordination and delivery support.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <FederalCTA source="federal_hero" />
            <Link to="/capability-statement" data-testid="federal-hero-capability" className="inline-flex items-center gap-2 font-[Poppins] font-semibold px-5 py-3.5 rounded-full border border-white/35 text-white hover:bg-white hover:text-[var(--pg-navy)] transition-colors"><FileText size={16} /> View Capability Statement</Link>
          </div>
        </div>
      </section>
      <section className="py-16 md:py-24 bg-white">
        <div className="pg-container">
          <SectionHead eyebrow="Federal Supply Capabilities" title="What we source and verify for federal requirements"
            sub="Documentation and compliance-related services are provided when applicable and when required by the solicitation or purchase order. PGPS does not represent itself as certified for requirements that have not been documented." />
          <div className="mt-10"><BulletGrid testidPrefix="federal-cap" items={FED_CAPS} /></div>
        </div>
      </section>
      <section className="py-16 md:py-24 bg-[var(--pg-bg)]" data-testid="federal-flow-section">
        <div className="pg-container">
          <SectionHead eyebrow="Federal / DLA Procurement Workflow" title="How PGPS handles a federal or prime-contractor requirement"
            sub="This is the actual lifecycle every Federal / DLA and Prime Contractor RFQ moves through inside the PGPS system. The same 15 stages appear as the status tracker in your Client Portal, and each stage is advanced by our team only when the work is actually done." />
          <div className="mt-10"><LifecycleFlow /></div>
        </div>
      </section>
      <section className="py-16 bg-white">
        <div className="pg-container grid md:grid-cols-3 gap-5">
          {[["/supplier-verification", FileSearch, "Supplier Verification & Sourcing", "How we research manufacturers, verify part numbers and CAGE information, and confirm availability."],
            ["/prime-contractors", ClipboardList, "Prime Contractor Support", "RFQ response, BOM sourcing and alternate-source research for primes and subcontractors."],
            ["/federal-compliance", Scale, "Federal Supply Compliance", "Which solicitation requirements we review and how supplier documentation is coordinated."]].map(([to, Icon, h, b]) => (
            <Link key={to} to={to} data-testid={`federal-link-${to.slice(1)}`} className="lift-card bg-[var(--pg-bg)] border border-slate-200 rounded-2xl p-6 block">
              <Icon size={22} className="text-[var(--pg-accent)]" />
              <h3 className="mt-3 font-[Poppins] font-bold text-[17px] text-[var(--pg-navy)]">{h}</h3>
              <p className="mt-2 text-[14px] text-[var(--pg-ink-soft)] leading-relaxed">{b}</p>
              <span className="mt-3 inline-flex items-center gap-1 text-[13.5px] font-[Poppins] font-semibold text-[var(--pg-navy)]">Read more <ArrowRight size={14} /></span>
            </Link>
          ))}
        </div>
      </section>
      <CTABand label="Submit a Federal Requirement" />
    </div>
  );
}

/* ---------------- /supplier-verification ---------------- */
const VERIFY_FLOW = ["Requirement", "Manufacturer Research", "Supplier Identification", "Part Number Verification", "CAGE / Manufacturer Verification", "Availability", "Lead Time", "Price", "Documentation", "Procurement"];
export function SupplierVerificationPage() {
  const desc = SEO_ROUTES["/supplier-verification"].description;
  return (
    <div data-testid="supplier-verification-page">
      <Seo route="/supplier-verification" jsonLd={[serviceSchema({ name: "Supplier Verification & Sourcing", description: desc, path: "/supplier-verification" }), crumbs("Supplier Verification", "/supplier-verification")]} />
      <PageHero testid="supplier-verification-hero" eyebrow="Supplier Verification & Sourcing" title="Supplier Verification &amp; Sourcing"
        sub="Before PGPS quotes a federal or commercial requirement, we research the manufacturer, identify potential commercial sources, and verify the available information about the part, the source, availability and lead time." />
      <section className="py-16 md:py-24 bg-white">
        <div className="pg-container">
          <SectionHead eyebrow="The PGPS Process" title="Ten checkpoints between your requirement and a purchase order" />
          <div className="mt-8"><FlowChain testid="verification-flow" items={VERIFY_FLOW} /></div>
          <div className="mt-12 grid md:grid-cols-2 gap-5">
            {[["Manufacturer research", "We identify the manufacturer associated with the NSN, part number or specification and research current production status, successor part numbers and known distributors."],
              ["Supplier identification", "We identify manufacturers, distributors and other potential commercial sources for the item. Suppliers are described by the documentation they provide — we do not label a source as authorized, approved, qualified or the manufacturer unless documentation supports it."],
              ["Part number & CAGE verification", "We cross-check the part number, revision and manufacturer against the information available to us, and confirm CAGE information when applicable to the requirement."],
              ["Availability, lead time & price", "Each source is asked for stock status, location, realistic ship date, unit price and any minimum quantity or packaging constraint, so comparisons are made on the same basis."],
              ["Documentation", "When required by the solicitation or purchase order, we request the applicable supplier documentation — for example certificates of conformance, traceability information or packaging details — and record what was and was not provided."],
              ["Procurement", "After your award or approval, PGPS issues the supplier purchase order, tracks inspection/documentation steps when applicable, and coordinates shipping and delivery."]].map(([h, b], i) => (
              <div key={h} data-testid={`verification-block-${i}`} className="bg-[var(--pg-bg)] border border-slate-200 rounded-2xl p-6">
                <h3 className="font-[Poppins] font-semibold text-[17px] text-[var(--pg-navy)]">{h}</h3>
                <p className="mt-2 text-[14.5px] text-[var(--pg-ink-soft)] leading-relaxed">{b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      <CTABand label="Submit a Requirement" />
    </div>
  );
}

/* ---------------- /prime-contractors ---------------- */
export function PrimeContractorsPage() {
  const desc = SEO_ROUTES["/prime-contractors"].description;
  return (
    <div data-testid="prime-contractors-page">
      <Seo route="/prime-contractors" jsonLd={[serviceSchema({ name: "Federal Prime Contractor Support", description: desc, path: "/prime-contractors" }), crumbs("Prime Contractors", "/prime-contractors")]} />
      <section data-testid="prime-hero" className="pt-[130px] pb-16 hero-bg">
        <div className="pg-container max-w-4xl">
          <span className="eyebrow" style={{ color: "#7EC4FF" }}>Federal Prime Contractor Support</span>
          <h1 className="mt-5 text-[36px] md:text-[50px] font-extrabold leading-[1.06] text-white">Federal Prime Contractor &amp; Subcontracting Support</h1>
          <p className="mt-5 text-[17px] text-white/80 max-w-3xl leading-relaxed">
            PGPS supports prime contractors with supplier identification, NSN and part-number sourcing, MRO procurement, equipment and component sourcing, competitive supplier quotes and delivery coordination.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/request-a-quote?type=prime" data-testid="prime-hero-cta" className="btn-primary" onClick={() => track("submit_rfq_clicked", { source: "prime_hero" })}>Send PGPS Your RFQ / BOM <ArrowRight size={18} /></Link>
            <Link to="/capability-statement" data-testid="prime-hero-capability" className="inline-flex items-center gap-2 font-[Poppins] font-semibold px-5 py-3.5 rounded-full border border-white/35 text-white hover:bg-white hover:text-[var(--pg-navy)] transition-colors"><FileText size={16} /> Capability Statement</Link>
          </div>
        </div>
      </section>
      <section className="py-16 md:py-24 bg-white">
        <div className="pg-container">
          <SectionHead eyebrow="What We Do for Primes" title="Support across the bid and the build" />
          <div className="mt-10"><BulletGrid testidPrefix="prime-item" items={["RFQ response support", "BOM sourcing", "NSN sourcing", "Part-number sourcing", "Supplier identification", "Competitive supplier quotes",
            "Availability verification", "Lead-time verification", "Alternate-source research", "Procurement coordination", "Logistics coordination", "Documentation management"]} /></div>
          <Prose testid="prime-prose">
            <p>Send the RFQ or bill of materials as-is — spreadsheet, PDF or solicitation attachment. We return sourcing options line by line with manufacturer, part number, availability, lead time and price, flag long-lead or obsolete items early, and research alternates you can present to your customer for approval.</p>
          </Prose>
        </div>
      </section>
      <CTABand label="Send PGPS Your RFQ / BOM" />
    </div>
  );
}

/* ---------------- /federal-compliance ---------------- */
const COMPLIANCE_ITEMS = ["Solicitation requirements", "Product identification", "NSN", "Part number", "Manufacturer", "CAGE", "Quantity", "Delivery", "Inspection/acceptance",
  "Packaging/marking", "Traceability", "Country-of-origin requirements", "Required certifications", "Supplier documentation", "Cybersecurity requirements when applicable"];
export function FederalCompliancePage() {
  const desc = SEO_ROUTES["/federal-compliance"].description;
  return (
    <div data-testid="federal-compliance-page">
      <Seo route="/federal-compliance" jsonLd={[serviceSchema({ name: "Federal Supply Compliance Review", description: desc, path: "/federal-compliance" }), crumbs("Federal Compliance", "/federal-compliance")]} />
      <PageHero testid="compliance-hero" eyebrow="Federal Supply Compliance" title="Federal Supply Compliance"
        sub="PGPS reviews applicable solicitation requirements and coordinates with suppliers to obtain required documentation when applicable." />
      <section className="py-16 md:py-24 bg-white">
        <div className="pg-container">
          <SectionHead eyebrow="What We Review" title="Requirements PGPS reviews on each federal requirement"
            sub="The review confirms what the solicitation or purchase order asks for and what each supplier can document. PGPS does not independently certify compliance with requirements unless documentation supports that claim." />
          <div className="mt-10"><BulletGrid testidPrefix="compliance-item" items={COMPLIANCE_ITEMS} /></div>
          <div className="mt-12 grid md:grid-cols-3 gap-5">
            {[[ClipboardList, "1. Identify", "Applicable requirements are pulled from the solicitation, clauses and purchase order and listed against each line item."],
              [Package, "2. Coordinate", "Suppliers are asked for the documentation the requirement calls for — certificates, traceability, packaging and marking details, country of origin."],
              [ShieldCheck, "3. Record", "What was provided, what was not, and any open items are recorded on the RFQ so the buyer can make an informed decision before award."]].map(([Icon, h, b], i) => (
              <div key={h} data-testid={`compliance-step-${i}`} className="bg-[var(--pg-bg)] border border-slate-200 rounded-2xl p-6">
                <div className="w-11 h-11 rounded-lg bg-[var(--pg-navy)] text-white flex items-center justify-center"><Icon size={20} /></div>
                <h3 className="mt-4 font-[Poppins] font-semibold text-[17px] text-[var(--pg-navy)]">{h}</h3>
                <p className="mt-2 text-[14px] text-[var(--pg-ink-soft)] leading-relaxed">{b}</p>
              </div>
            ))}
          </div>
          <div className="mt-10 flex flex-wrap gap-3 items-center"><FederalCTA source="compliance" /><CapabilityDownload /></div>
        </div>
      </section>
      <CTABand label="Submit a Federal Requirement" />
    </div>
  );
}
